/**
 * Add Role - Club admin interface for adding new roles
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, X } from 'lucide-react';
import apiService from '../../services/api';
import {
  AdminPageLayout,
  FormInput,
  FormTextArea,
  FormCheckbox,
  Button,
  FormSelect,
  Section,
  Card,
  Grid,
  Col
} from '../../components';
import { FormActionGroup } from '../../components/admin/patterns';
import { ErrorBanner } from '../../components/common';
import { useFormValidation } from '../../hooks/useFormValidation';
import { useToast } from '../../contexts/ToastContext';

const AddRole = () => {
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  const [error, setError] = useState(null);
  const [roleTemplates, setRoleTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);

  useEffect(() => {
    loadRoleTemplates();
  }, []);

  const loadRoleTemplates = async () => {
    try {
      setTemplatesLoading(true);
      const response = await apiService.getRoleTemplates();
      setRoleTemplates(response.templates || []);
      setTemplatesLoading(false);
    } catch (err) {
      console.error('Error loading role templates:', err);
      setRoleTemplates([]);
      setTemplatesLoading(false);
    }
  };

  const availablePermissions = [
    { id: 'user_management', name: 'User Management', description: 'Create, edit, and manage club users' },
    { id: 'program_management', name: 'Program Management', description: 'Manage training programs and schedules' },
    { id: 'player_evaluation', name: 'Player Evaluation', description: 'Conduct and view player assessments' },
    { id: 'skills_assessment', name: 'Skills Assessment', description: 'Evaluate player skills and progress' },
    { id: 'document_management', name: 'Document Management', description: 'Manage documents and requirements' },
    { id: 'reports', name: 'Reports', description: 'View club analytics and performance reports' },
    { id: 'settings', name: 'Club Settings', description: 'Configure club preferences and settings' },
    { id: 'registration', name: 'Registration', description: 'Manage player registrations' },
    { id: 'view_child_progress', name: 'View Child Progress', description: 'View progress of registered children' },
    { id: 'program_participation', name: 'Program Participation', description: 'Participate in assigned programs' }
  ];

  const {
    formData,
    errors,
    touched,
    isSubmitting,
    handleFieldChange,
    handleFieldBlur,
    handleSubmit: handleFormSubmit,
    setFieldValue
  } = useFormValidation({
    initialValues: {
      selectedTemplate: '',
      name: '',
      description: '',
      permissions: [],
      is_active: true,
      is_staff: false
    },
    validationSchema: (values) => {
      const newErrors = {};
      
      if (!values.name.trim()) {
        newErrors.name = 'Role name is required';
      }
      
      if (!values.description.trim()) {
        newErrors.description = 'Description is required';
      }
      
      if (values.permissions.length === 0) {
        newErrors.permissions = 'At least one permission is required';
      }
      
      return newErrors;
    },
    onSubmit: async (values) => {
      try {
        const roleData = {
          name: values.name,
          description: values.description,
          permissions: values.permissions,
          is_custom: true,
          is_active: values.is_active,
          is_staff: values.is_staff
        };

        await apiService.createClubRole(roleData);
        showSuccess(`Role "${values.name}" created successfully`);
        navigate('/admin/roles');
      } catch (err) {
        const errorMsg = err.message || 'Failed to create role';
        setError(errorMsg);
        showError(errorMsg);
        throw err;
      }
    }
  });

  const handlePermissionToggle = (permissionId) => {
    const currentPermissions = formData.permissions || [];
    const newPermissions = currentPermissions.includes(permissionId)
      ? currentPermissions.filter(p => p !== permissionId)
      : [...currentPermissions, permissionId];
    
    setFieldValue('permissions', newPermissions);
  };

  const handleTemplateChange = (templateId) => {
    setFieldValue('selectedTemplate', templateId);
    
    if (templateId) {
      const selectedTemplate = roleTemplates.find(t => t.id === templateId);
      if (selectedTemplate) {
        setFieldValue('name', selectedTemplate.name);
        setFieldValue('description', selectedTemplate.description);
        setFieldValue('permissions', [...selectedTemplate.permissions]);
        setFieldValue('is_staff', selectedTemplate.is_staff);
        setFieldValue('is_active', true); // New roles are active by default
      }
    } else {
      // Clear form when no template selected
      setFieldValue('name', '');
      setFieldValue('description', '');
      setFieldValue('permissions', []);
      setFieldValue('is_staff', false);
      setFieldValue('is_active', true);
    }
  };

  return (
    <AdminPageLayout
      title="ADD NEW ROLE"
      subtitle="Create a new role with custom permissions"
      backPath="/admin/roles"
      maxWidth="1400px"
    >
      {error && (
        <ErrorBanner
          type="error"
          message={error}
          onDismiss={() => setError(null)}
        />
      )}

      <form onSubmit={handleFormSubmit}>
        <Section title="COPY FROM TEMPLATE (OPTIONAL)">
          <Card level={1}>
            <p style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)', marginBottom: '1.5rem' }}>
              Select a system role template to start with predefined permissions, or leave blank to create from scratch.
            </p>

            <FormSelect
              label="Role Template"
              name="selectedTemplate"
              value={formData.selectedTemplate}
              onChange={(e) => handleTemplateChange(e.target.value)}
              options={[
                { value: '', label: 'Create from scratch' },
                ...roleTemplates.map(template => ({
                  value: template.id,
                  label: `${template.name} (${template.permissions.length} permissions)`
                }))
              ]}
              disabled={templatesLoading}
            />

            {formData.selectedTemplate && (
              <div style={{
                marginTop: '1rem',
                padding: '1rem',
                backgroundColor: 'var(--surface-lvl2)',
                borderRadius: '4px'
              }}>
                <small style={{ color: 'var(--color-text-secondary)' }}>
                  Template selected: {roleTemplates.find(t => t.id === formData.selectedTemplate)?.description}
                </small>
              </div>
            )}
          </Card>
        </Section>

        <Section title="ROLE INFORMATION">
          <Card level={1}>
            <Grid columns={2} gap="2rem">
              <Col>
                <FormInput
                  label="Role Name"
                  name="name"
                  value={formData.name}
                  onChange={handleFieldChange}
                  onBlur={handleFieldBlur}
                  error={touched.name ? errors.name : ''}
                  placeholder="e.g., Assistant Coach, Team Manager"
                  required
                />
              </Col>
              <Col span={2}>
                <FormTextArea
                  label="Description"
                  name="description"
                  value={formData.description}
                  onChange={handleFieldChange}
                  onBlur={handleFieldBlur}
                  error={touched.description ? errors.description : ''}
                  placeholder="Describe the role and its responsibilities"
                  rows={3}
                  required
                />
              </Col>
            </Grid>
          </Card>
        </Section>

        <Section title="PERMISSIONS">
          <Card level={1}>
            {touched.permissions && errors.permissions && (
              <ErrorBanner type="error" message={errors.permissions} />
            )}

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '1rem' }}>
              {availablePermissions.map(permission => (
                <div
                  key={permission.id}
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '0.75rem',
                    padding: '1rem',
                    backgroundColor: 'var(--surface-lvl2)',
                    borderRadius: '4px'
                  }}
                >
                  <FormCheckbox
                    label=""
                    name={`permission-${permission.id}`}
                    checked={formData.permissions.includes(permission.id)}
                    onChange={() => handlePermissionToggle(permission.id)}
                  />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: '500', color: 'var(--color-text)', marginBottom: '0.25rem' }}>
                      {permission.name}
                    </div>
                    <div style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)' }}>
                      {permission.description}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </Section>

        <Section title="STATUS">
          <Card level={1}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
              <div>
                <FormCheckbox
                  label="Active"
                  name="is_active"
                  checked={formData.is_active}
                  onChange={(e) => setFieldValue('is_active', e.target.checked)}
                />
                <p style={{
                  fontSize: '0.875rem',
                  color: 'var(--color-text-secondary)',
                  marginTop: '0.25rem',
                  marginLeft: '1.75rem'
                }}>
                  Inactive roles cannot be assigned to users
                </p>
              </div>

              <div>
                <FormCheckbox
                  label="Staff Role"
                  name="is_staff"
                  checked={formData.is_staff}
                  onChange={(e) => setFieldValue('is_staff', e.target.checked)}
                />
                <p style={{
                  fontSize: '0.875rem',
                  color: 'var(--color-text-secondary)',
                  marginTop: '0.25rem',
                  marginLeft: '1.75rem'
                }}>
                  Staff roles are for club employees (admins, coaches, directors, managers). Used for event visibility filtering.
                </p>
              </div>
            </div>
          </Card>
        </Section>

        <FormActionGroup
          onCancel={() => navigate('/admin/roles')}
          onSubmit={handleFormSubmit}
          submitLabel={isSubmitting ? 'CREATING...' : 'CREATE ROLE'}
          isSubmitting={isSubmitting}
        />
      </form>
    </AdminPageLayout>
  );
};

export default AddRole;

